import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWebSocket } from 'context/WebSocketConnectionContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getAutoSaveDraft } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';

const DEBOUNCE_MS = 1000;

export function useAutoSave({
  caseId = '',
  autoSaveType = 'thread',
  messageThreadId = '',
  getFormValues,
  formValues,
  isDirty = false,
  enabled = true,
} = {}) {
  const { send } = useWebSocket();
  const { firmDetails } = useUserDetailsContext();

  const timerRef = useRef(null);
  const [hasDraft, setHasDraft] = useState(false);

  const isReply = autoSaveType === 'reply';

  const { data: draftData } = useQuery({
    queryKey: messageQueryKeys.autoSave.byCaseId(caseId),
    queryFn: () => getAutoSaveDraft(caseId),
    enabled: enabled && !isReply,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    select: (res) => res?.data || null,
  });

  useEffect(() => {
    if (draftData && Object.keys(draftData).length > 0) setHasDraft(true);
  }, [draftData]);

  const sendDraft = useCallback(
    (data, isDelete = false) => {
      if (!send) return;
      try {
        send({
          action: 'autosave',
          auto_save_type: autoSaveType,
          type: isReply ? 'autosave_message_reply' : 'autosave_message',
          is_delete: isDelete,
          case_id: caseId,
          data,
          user_id: firmDetails?.current_user,
          tenant_id: firmDetails?.tenant_id,
          ...(firmDetails?.firm_user_id && { firm_user_id: firmDetails.firm_user_id }),
          ...(firmDetails?.contact_id && { contact_id: firmDetails.contact_id }),
          ...(isReply && messageThreadId && { message_thread_id: messageThreadId }),
        });
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    },
    [send, firmDetails, autoSaveType, isReply, caseId, messageThreadId]
  );

  const serializedValues = useMemo(() => JSON.stringify(formValues ?? {}), [formValues]);

  // Keep latest callbacks in refs — no need to include them as deps.
  const sendDraftRef = useRef(sendDraft);
  sendDraftRef.current = sendDraft;
  const getFormValuesRef = useRef(getFormValues);
  getFormValuesRef.current = getFormValues;

  useEffect(() => {
    if (!enabled || !isDirty) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      sendDraftRef.current(getFormValuesRef.current?.());
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [enabled, isDirty, serializedValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearDraft = useCallback(() => {
    clearTimeout(timerRef.current);
    sendDraftRef.current(null, true);
    setHasDraft(false);
  }, []);

  const saveDraft = useCallback((data) => {
    sendDraftRef.current(data);
  }, []);

  return { draftData, hasDraft, clearDraft, saveDraft };
}
